"use client";

import {
  IDedicatedResourceRef,
  WorkplaceRequestElement,
  WorkplaceRequestTypeElement,
  WorkplaceRequestWorkflowRun
} from "@gettactic/api";
import { useUserListByIds } from "@gettactic/hooks";
import { useCallback, useMemo } from "react";
import {
  type DeskAutomationStep,
  type DeskMove,
  deskAutomationContextOf,
  deskMoveOf,
  hasReportedOutcome,
  resolveDeskAutomation,
  type VacatedDesk,
  vacatedDedicatedDesk
} from "../lib/desk-automation";

export type DeskAutomationRuns = ReadonlyMap<
  string,
  WorkplaceRequestWorkflowRun
>;

/**
 * The desk rules one request will run — or has run — resolved against it.
 * Runs are keyed by the workflow they belong to; a rule that has not run yet
 * simply has none.
 */
export const useDeskAutomationSteps = (
  request: WorkplaceRequestElement,
  type: WorkplaceRequestTypeElement | undefined
): { steps: DeskAutomationStep[]; runByWorkflowId: DeskAutomationRuns } => {
  const context = useMemo(() => deskAutomationContextOf(request), [request]);

  const steps = useMemo(
    () =>
      resolveDeskAutomation(type?.workflows, context).filter(
        (step) => step.enabled
      ),
    [type?.workflows, context]
  );

  const runByWorkflowId = useMemo(
    () =>
      new Map(
        (request.workflow_runs ?? []).map((run) => [run.workflow.id, run])
      ),
    [request.workflow_runs]
  );

  return { steps, runByWorkflowId };
};

/**
 * Looks up, for each dedicated move that hasn't run yet, the desk the person
 * is moving out of. Before the move there is no record to read, so it has to
 * be found on the person; core exposes a user's dedicated desks and nothing
 * equivalent for their assignments, so only a dedicated move can be answered
 * this way — see {@link vacatedDedicatedDesk}. Until the lookup has answered
 * every move reads as unknown.
 */
export const useVacatedDedicatedDesk = (
  steps: DeskAutomationStep[],
  runByWorkflowId: DeskAutomationRuns
): ((step: DeskAutomationStep) => VacatedDesk<IDedicatedResourceRef>) => {
  const pendingDedicatedMoverIds = useMemo(
    () =>
      Array.from(
        new Set(
          steps
            .filter(
              (step) =>
                step.action === "assign" &&
                step.mode === "dedicated" &&
                !!step.personId &&
                !hasReportedOutcome(runByWorkflowId.get(step.workflowId))
            )
            .map((step) => step.personId)
            .filter((id): id is string => id !== null)
        )
      ),
    [steps, runByWorkflowId]
  );
  const moversQuery = useUserListByIds(pendingDedicatedMoverIds);

  return useCallback(
    (step: DeskAutomationStep): VacatedDesk<IDedicatedResourceRef> => {
      if (!step.personId) return { kind: "unknown" };
      const mover = moversQuery.data?.find(
        (user) => user?.id === step.personId
      );
      // No answer yet, or a person the lookup couldn't return: not "nothing
      // to free", just not known.
      const held = mover?.dedicated_resources;
      if (!held) return { kind: "unknown" };

      const vacated = vacatedDedicatedDesk(
        step,
        held.map((resource) => resource.id)
      );
      if (vacated.kind !== "known") return vacated;
      const desk = held.find((resource) => resource.id === vacated.desk);
      return desk ? { kind: "known", desk } : { kind: "unknown" };
    },
    [moversQuery.data]
  );
};

/**
 * The relocation a request describes, when it unambiguously describes one:
 * the desk the person gives up and the one they take. `null` for anything
 * else — a new hire, a release, a move whose old desk can't be named.
 */
export const useDeskMove = (
  request: WorkplaceRequestElement,
  type: WorkplaceRequestTypeElement | undefined
): DeskMove | null => {
  const { steps, runByWorkflowId } = useDeskAutomationSteps(request, type);
  const vacatedDesk = useVacatedDedicatedDesk(steps, runByWorkflowId);

  return useMemo(
    () =>
      deskMoveOf(steps, runByWorkflowId, (step) => {
        const vacated = vacatedDesk(step);
        return vacated.kind === "known" ? vacated.desk.id : null;
      }),
    [steps, runByWorkflowId, vacatedDesk]
  );
};
